import { Instance, applySnapshot, flow, types } from "mobx-state-tree";
import api from "services/api";
import { MSURL } from "utils/ms";

// Define DataManualBadge model
const DataManualBadge = types.model("ManualBadge", {
  title: types.optional(types.string, ""),
  name: types.optional(types.string, ""),
  fileUrl: types.optional(types.string, ""),
  displayDate: types.optional(types.string, ""),
  publishStatus: types.optional(types.string, ""),
  successfulUsersAmount: types.optional(types.integer, 0),
});

const DataManualBadgeList = types.model("ManualBadges", {
  id: types.integer,
  title: types.optional(types.string, ""),
  fileUrl: types.optional(types.string, ""),
  displayDate: types.optional(types.string, ""), // assuming datetime as string
  PublishStatus: types.optional(types.string, ""),
  successfulUsersAmount: types.optional(types.integer, 0),
});

// Define context model for pagination
const Context = types.model({
  page: types.integer,
  size: types.integer,
  pages: types.integer,
  totalRows: types.integer,
});

// Define response model for manual badge list
const ManualBadgeListResponse = types.model({
  context: Context,
  data: types.array(DataManualBadgeList),
  message: types.optional(types.string, ""),
  success: types.boolean,
});

const Translation = types.model({
  id: types.integer,
  localeId: types.optional(types.string, ""),
  title: types.optional(types.string, ""),
  description: types.optional(types.string, ""),
});

const ManualBadgeResponse = types.model({
  success: types.boolean,
  data: types.model({
    id: types.integer,
    dataManualBadge: types.optional(DataManualBadge, {}),
    translations: types.array(Translation),
  }),
  message: types.optional(types.string, ""),
});

const ManualBadgePostResponse = types.model({
  success: types.boolean,
});

const ManualBadgeCreateRequest = types.model({
  dataManualBadge: types.model({
    name: types.optional(types.string, ""),
    displayDate: types.optional(types.string, ""),
    manualBadgeBlobFileId: types.optional(types.string, ""),
  }),
  translations: types.array(
    types.model({
      localeId: types.optional(types.string, ""),
      title: types.optional(types.string, ""),
      description: types.optional(types.string, ""),
    }),
  ),
});

const ManualBadgeUpdateRequest = types.model({
  dataManualBadge: types.model({
    name: types.optional(types.string, ""),
    displayDate: types.optional(types.string, ""), // assuming datetime as string
    manualBadgeBlobFileId: types.optional(types.string, ""),
  }),
  id: types.optional(types.integer, 0),
  translations: types.array(
    types.model({
      localeId: types.optional(types.string, ""),
      title: types.optional(types.string, ""),
      description: types.optional(types.string, ""),
    }),
  ),
});

const UserFileUploadRequest = types.model({
  id: types.maybe(types.number),
  emails: types.array(types.string),
});

const UserFileUploadResponse = types.model({
  success: types.boolean,
  data: types.model({
    emailUnsuccessfuls: types.array(
      types.model({
        email: types.optional(types.string, ""),
        reason: types.optional(types.string, ""),
      }),
    ),
    usersSuccessfulyLoaded: types.integer,
  }),
});

const PatchResponse = types.model({
  success: types.boolean,
});

const ManualBadgeStore = types
  .model({
    isUpdating: types.optional(types.boolean, false),
    manualBadge: types.maybe(ManualBadgeResponse),
    manualBadges: types.optional(types.array(DataManualBadgeList), []),
  })
  .actions(self => ({
    fetchManualBadgeList: flow(function*(params) {
      self.isUpdating = true;
      try {
        const queryParams = new URLSearchParams(params).toString();
        const response: Instance<typeof ManualBadgeListResponse> = yield api.get(
          `/loggingtracking/api/v1/cms/manual-badges?${queryParams}`,
          undefined,
          undefined,
          MSURL,
        );
        if (response.success) {
          applySnapshot(self, { ...self, manualBadges: response.data });
          console.log("Manual Badges fetched successfully:", response);
        } else {
          console.error("Failed to fetch manual badges");
        }
      } catch (error) {
        console.error("An error occurred while fetching manual badges:", error);
        throw error;
      } finally {
        self.isUpdating = false;
      }
    }),
    fetchManualBadge: flow(function*(id) {
      self.isUpdating = true;
      try {
        const response: Instance<typeof ManualBadgeResponse> = yield api.get(
          `/loggingtracking/api/v1/cms/manual-badges/${id}`,
          undefined,
          undefined,
          MSURL,
        );
        if (response.success) {
          applySnapshot(self, { ...self, manualBadge: response });
        } else {
          console.error("Failed to fetch manual badge");
        }
      } catch (error) {
        console.error("An error occurred while fetching manual badge:", error);
        throw error;
      } finally {
        self.isUpdating = false;
      }
    }),
    updateManualBadgeStatus: flow(function*(id, status) {
      try {
        const payload = { entityType: "ManualBadge", publishStatus: status };
        const response: Instance<typeof PatchResponse> = yield api.patch(
          `/loggingtracking/api/v1/cms/entity/${id}`,
          payload,
          undefined,
          MSURL,
        );
        if (response.success) {
          const updatedBadges = self.manualBadges.map(badge =>
            badge.id === id ? { ...badge, PublishStatus: status } : badge,
          );
          applySnapshot(self, { ...self, manualBadges: updatedBadges });
        } else {
          console.error("Failed to update manual badge status");
        }
      } catch (error) {
        console.error(
          "An error occurred while updating manual badge status:",
          error,
        );
        throw error;
      }
    }),
    createManualBadge: flow(function*(manualBadgeData) {
      try {
        const payload = ManualBadgeCreateRequest.create(manualBadgeData);
        const response: Instance<typeof ManualBadgePostResponse> = yield api.post(
          `/loggingtracking/api/v1/cms/manual-badges`,
          payload,
          undefined,
          MSURL,
        );
        if (!response.success) {
          console.error("Failed to create manual badge");
        }
      } catch (error) {
        console.error("An error occurred while creating manual badge:", error);
        throw error;
      }
    }),
    updateManualBadge: flow(function*(manualBadgeData) {
      try {
        const payload = ManualBadgeUpdateRequest.create(manualBadgeData);
        const response: Instance<typeof ManualBadgePostResponse> = yield api.put(
          `/loggingtracking/api/v1/cms/manual-badges`,
          payload,
          undefined,
          MSURL,
        );
        if (!response.success) {
          console.error(`Failed to update manual badge ${manualBadgeData.id}`);
        }
      } catch (error) {
        console.error(
          `An error occurred while updating manual badge ${manualBadgeData.id}:`,
          error,
        );
        throw error;
      }
    }),
    uploadUsersFile: flow(function*(id, emails) {
      try {
        const payload = UserFileUploadRequest.create({ emails });
        const response: Instance<typeof UserFileUploadResponse> = yield api.post(
          `/loggingtracking/api/v1/cms/manual-badges/${id}/emails`,
          payload,
          undefined,
          MSURL,
        );
        if (!response.success) {
          console.error(`Failed to upload users file for Manual Badge ${id}`);
        }
      } catch (error) {
        console.error(
          `An error occurred while uploading users file for Manual Badge ${id}:`,
          error,
        );
        throw error;
      }
    }),
    checkUsersEmails: flow(function*(id, emails) {
      try {
        const payload = UserFileUploadRequest.create({ emails });
        const response: Instance<typeof UserFileUploadResponse> = yield api.post(
          `/loggingtracking/api/v1/cms/manual-badges/${id}/check-emails`,
          payload,
          undefined,
          MSURL,
        );
        if (response.success) {
          return response.data;
        } else {
          console.error(`Failed to check users file for Manual Badge ${id}`);
          return undefined;
        }
      } catch (error) {
        console.error(
          `An error occurred while checking users file for Manual Badge ${id}:`,
          error,
        );
        throw error;
      }
    }),
  }));

export default ManualBadgeStore.create();
